/**
 * The modal shell, in one place.
 *
 * ⚠️ IT EXISTS BECAUSE THE PUBLISH WIZARD WOULD HAVE BEEN A SECOND COPY OF THE ARRIVAL DIALOG —
 * the veil, the blur, the backdrop-as-a-button, the Escape key and the focus move, all of which are
 * easy to write and easy to write two-thirds of. `LanguageFill` draws the same furniture and is
 * deliberately NOT one of these: it is `role="status"`, because it reports a thing in progress
 * rather than asking a question, and it must not be dismissible.
 *
 * ⚠️ THE CSS IS STILL `ed__*` AND STILL LIVES IN `editor.css`, DELIBERATELY. `@ksa/ui` would be the
 * tidier home and it is not worth the move: the blur, the ground under it and the panel's size were
 * tuned against the editor's own screen, and the classes are named in the browser suite. What was
 * duplicated was the BEHAVIOUR, and that is what moved.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { useLang } from '@ksa/ui'

export function Dialog({
  labelledBy,
  onClose,
  children,
}: {
  /** The id of the heading inside. A dialog whose label is a `aria-label` duplicates a title that is
   *  already on screen, and the two drift. */
  labelledBy: string
  /** Absent for a dialog that cannot be dismissed — the translation progress, which is a state
   *  rather than a question. Escape and the backdrop follow it: neither exists when this does not. */
  onClose?: () => void
  children: ReactNode
}) {
  const { t } = useLang()
  const panel = useRef<HTMLDivElement>(null)

  /**
   * ⚠️ ESCAPE CLOSES IT, BECAUSE ESCAPE CLOSES THINGS. The full-screen preview already honours it for
   * the same reason: a customer should never have to hunt for the way out of something they did not
   * ask to open.
   */
  useEffect(() => {
    if (!onClose) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Focus lands inside, so a keyboard or screen-reader user is where the dialog is rather than
  // still on the page behind it.
  useEffect(() => {
    panel.current?.focus()
  }, [])

  return (
    <div className="ed__veil" role="presentation">
      {/*
        ⚠️ THE BACKDROP IS A BUTTON, so a tap anywhere outside closes it — the gesture everybody
        already expects — and it is unlabelled to a screen reader's tab order only when there is
        nothing for it to do.
      */}
      {onClose ? (
        <button type="button" className="ed__veil__backdrop" aria-label={t('Close', 'إغلاق')} onClick={onClose} />
      ) : (
        <div className="ed__veil__backdrop" />
      )}

      <div ref={panel} className="ed__hello" role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1}>
        {children}
      </div>
    </div>
  )
}
